import { useState } from 'react'
import { KeyRound, Check, X, UserPlus } from 'lucide-react'
import { LoginsEditor, validateLogins, type LoginEntry } from '../qaConfig'

// An inline form the chat agent can request (via the `form` stream event) so the user fills in login
// credentials in a proper form — with masked password fields (show/hide) — instead of typing secrets
// into the chat transcript. These become the project's login global variables. For chat onboarding,
// agents can always self-register test accounts (they simply prefer any provided login), so the
// "let agents register their own accounts" toggle is hidden and that setting is always on.
//
// When `onSelfSignup` is provided, we also surface a button letting the user skip entering
// credentials entirely and have the agent register its own test account instead — used by the
// onboarding login step when a site is detected to be behind a login wall.
export default function ChatLoginsForm({
  title = 'Add a test login',
  initialLogins = [],
  onSubmit,
  onCancel,
  onSelfSignup,
}: {
  title?: string
  initialLogins?: LoginEntry[]
  onSubmit: (result: { logins: LoginEntry[]; autoCreateAccount: boolean }) => void
  onCancel: () => void
  onSelfSignup?: () => void
}) {
  const [logins, setLogins] = useState<LoginEntry[]>(initialLogins.length ? initialLogins : [{ email: '', password: '' }])
  const [errors, setErrors] = useState<Record<string, string>>({})

  function submit() {
    const validationErrors = validateLogins(logins)
    if (Object.keys(validationErrors).length) { setErrors(validationErrors); return }
    const filled = logins.filter((l) => l.email.trim() && l.password.trim())
    // autoCreateAccount is always true for chat: agents may self-register but prefer provided logins.
    onSubmit({ logins: filled, autoCreateAccount: true })
  }

  return (
    <div className="chat-form" data-testid="chat-logins-form">
      <div className="chat-form__head">
        <KeyRound size={15} strokeWidth={1.75} aria-hidden />
        <span className="chat-form__title">{title}</span>
      </div>
      <p className="chat-form__hint">
        Credentials are saved as the project&apos;s login variables and used to test signed-in areas. Passwords are
        entered here, not in the chat.
      </p>
      <LoginsEditor
        logins={logins}
        onLoginsChange={(next) => { setLogins(next); setErrors({}) }}
        autoCreateAccount
        onAutoCreateChange={() => {}}
        errors={errors}
        maskPassword
        hideAutoCreate
      />
      <div className="chat-form__actions">
        <button type="button" className="chat-form__submit" onClick={submit} data-testid="chat-logins-submit" data-analytics-event="chat-logins-submit">
          <Check size={14} strokeWidth={2} aria-hidden /> Save logins
        </button>
        <button type="button" className="chat-form__cancel" onClick={onCancel} data-testid="chat-logins-cancel" data-analytics-event="chat-logins-cancel">
          <X size={14} strokeWidth={2} aria-hidden /> Cancel
        </button>
      </div>
      {onSelfSignup && (
        <button
          type="button"
          className="chat-form__self-signup"
          onClick={onSelfSignup}
          data-testid="chat-logins-self-signup"
          data-analytics-event="chat-logins-self-signup"
        >
          <UserPlus size={14} strokeWidth={2} aria-hidden /> Let the agent sign up for its own account
        </button>
      )}
    </div>
  )
}
